import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Tabs, Tab, TextField, MenuItem,
  Button, Alert, Table, TableBody, TableCell, TableHead, TableRow,
  Checkbox, Chip, IconButton, Tooltip, CircularProgress, Divider, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Home, PlayArrow, Refresh, HowToReg, Receipt,
  TrendingUp, School, CheckBox, CheckBoxOutlineBlank, SchoolOutlined, Visibility as Eye,
  CheckCircle, ErrorOutline
} from '@mui/icons-material';
import axios from 'axios';
import { fmtMoney } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TERMS = [1, 2, 3];

// ── helpers ────────────────────────────────────────────────────────────────────

// Known result fields → labelled stat tiles. Order defines display order; only
// fields actually present on the result are shown.
const STAT_FIELDS = [
  { key: 'registered',        label: 'Newly registered',  color: '#2e7d32' },
  { key: 'alreadyRegistered', label: 'Already registered', color: '#757575' },
  { key: 'updated',           label: 'Learners updated',  color: '#1565c0' },
  { key: 'totalAreasAdded',   label: 'Areas added',       color: '#2e7d32', prefix: '+' },
  { key: 'totalAreasRemoved', label: 'Areas removed',     color: '#c62828', prefix: '−' },
  { key: 'invoiced',          label: 'Invoices created',  color: '#2e7d32' },
  { key: 'toppedUp',          label: 'Topped up',         color: '#1565c0' },
  { key: 'alreadyInvoiced',   label: 'Already invoiced',  color: '#757575' },
  { key: 'noTemplate',        label: 'No fee template',   color: '#e65100' },
  { key: 'totalAmount',       label: 'Total invoiced',    color: '#00695c', money: true },
  { key: 'promoted',          label: 'Promoted',          color: '#2e7d32' },
  { key: 'graduated',         label: 'Graduated',         color: '#2e7d32' },
  { key: 'total',             label: 'Total processed',   color: '#37474f' },
];

function StatTile({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, minWidth: 96, textAlign: 'center', borderColor: `${color}55`, bgcolor: `${color}0d` }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{label}</Typography>
    </Paper>
  );
}

function ResultSummary({ result, onClose }) {
  if (!result) return null;

  const errCount = result.errors?.length || 0;
  const hasErrors = errCount > 0;

  const tiles = STAT_FIELDS
    .filter(f => typeof result[f.key] === 'number')
    .map(f => ({
      ...f,
      display: f.money ? fmtMoney(result[f.key]) : `${f.prefix || ''}${result[f.key].toLocaleString()}`,
    }));
  if (hasErrors) tiles.push({ key: 'errors', label: 'Errors', color: '#c62828', display: String(errCount) });

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderColor: hasErrors ? 'warning.main' : 'success.main', borderLeftWidth: 4, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: hasErrors ? 'warning.50' : 'success.50' }}>
        {hasErrors ? <ErrorOutline color="warning" /> : <CheckCircle color="success" />}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          {hasErrors ? 'Operation completed with issues' : 'Operation complete'}
        </Typography>
        <Button size="small" onClick={onClose}>Dismiss</Button>
      </Box>

      <Box sx={{ p: 2 }}>
        {result.summary && (
          <Typography variant="body2" sx={{ mb: tiles.length ? 1.5 : 0 }}>{result.summary}</Typography>
        )}

        {tiles.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tiles.map(t => <StatTile key={t.key} label={t.label} value={t.display} color={t.color} />)}
          </Box>
        )}

        {!result.summary && tiles.length === 0 && (
          <Typography variant="body2" color="text.secondary">{result.message || 'Done.'}</Typography>
        )}

        {result.batchRef && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Batch reference: <strong>{result.batchRef}</strong>
          </Typography>
        )}

        {result.updatedLearners && result.updatedLearners.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Affected learners:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-word' }}>
              {result.updatedLearners.join(', ')}
              {result.moreCount > 0 && ` …and ${result.moreCount} more`}
            </Typography>
          </Box>
        )}

        {hasErrors && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>{errCount} error(s):</Typography>
            {result.errors.slice(0, 3).map((e, i) => (
              <Typography key={i} variant="caption" display="block" color="error" sx={{ mt: 0.25 }}>
                • {e.admNo}: {e.error}
              </Typography>
            ))}
            {errCount > 3 && (
              <Typography variant="caption" color="error" sx={{ mt: 0.25 }}>…and {errCount - 3} more</Typography>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ── Tab 1: Batch Term Registration ─────────────────────────────────────────────

function BatchRegistrationTab({ academicYears }) {
  const [year,    setYear]    = useState('');
  const [term,    setTerm]    = useState('');
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  const run = async () => {
    if (!year || !term) { setError('Select academic year and term'); return; }
    setRunning(true); setError(''); setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/academics/batch-register`,
        { academicYear: year, term }, { headers: getHeaders() });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally { setRunning(false); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Registers all Active learners who are not yet registered for the selected term.
        Skips learners already registered. Assigns each stage's <strong>mandatory</strong> learning
        areas only and fee template totals automatically — optional/elective subjects are added
        per learner afterwards in Term Registration (Learners → Term Registration).
      </Typography>

      {error  && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      <ResultSummary result={result} onClose={() => setResult(null)} />

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <TextField select label="Academic Year" value={year} onChange={e => setYear(e.target.value)}
          size="small" sx={{ minWidth: 160 }}>
          {academicYears.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
        </TextField>
        <TextField select label="Term" value={term} onChange={e => setTerm(e.target.value)}
          size="small" sx={{ minWidth: 120 }}>
          {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
        </TextField>
        <Button variant="contained" startIcon={running ? <CircularProgress size={16} color="inherit" /> : <HowToReg />}
          onClick={run} disabled={running || !year || !term}>
          {running ? 'Registering…' : 'Register All'}
        </Button>
      </Box>
    </Box>
  );
}

// ── Tab 2: Update Missing Learning Areas ──────────────────────────────────────

function UpdateMissingAreasTab({ academicYears, stages }) {
  const [year,      setYear]      = useState('');
  const [term,      setTerm]      = useState('');
  const [stage,     setStage]     = useState('');
  const [classCode, setClassCode] = useState('');
  const [studentType, setStudentType] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [running,   setRunning]   = useState(false);
  const [result,    setResult]    = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [error,     setError]     = useState('');

  const getFilters = () => ({
    academicYear: year,
    term,
    stageCode: stage || undefined,
    classCode: classCode || undefined,
    studentType: studentType || undefined
  });

  const runPreview = async () => {
    if (!year || !term) { setError('Select academic year and term'); return; }
    setPreviewing(true); setError(''); setPreview(null);
    try {
      const { data } = await axios.post(`${API}/api/academics/preview-missing-learning-areas`,
        getFilters(), { headers: getHeaders() });
      setPreview(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const runUpdate = async () => {
    if (!year || !term) { setError('Select academic year and term'); return; }
    setRunning(true); setError(''); setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/academics/update-missing-learning-areas`,
        getFilters(), { headers: getHeaders() });
      setResult(data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Update failed');
    } finally { setRunning(false); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Syncs each learner's term registration to the stage's <strong>Stage-Subjects</strong>: <strong>adds</strong> mandatory
        learning areas that are missing and <strong>removes</strong> any areas that no longer belong to the stage.
        Preview first to see how many records will be affected.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Use filters (Stage, Class, Student Type) to target specific groups and avoid processing all records at once.
        Stages with no subjects defined for the year are skipped (to avoid stripping every area).
      </Alert>

      {error  && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {preview && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Preview: {preview.summary}
          </Typography>
          {preview.toUpdate === 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              All learners already have the required learning areas.
            </Typography>
          )}
        </Alert>
      )}
      <ResultSummary result={result} onClose={() => setResult(null)} />

      <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2 }}>
        <TextField select label="Academic Year *" value={year} onChange={e => setYear(e.target.value)}
          size="small" fullWidth>
          {academicYears.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
        </TextField>
        <TextField select label="Term *" value={term} onChange={e => setTerm(e.target.value)}
          size="small" fullWidth>
          {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
        </TextField>
        <TextField select label="Stage (Grade)" value={stage} onChange={e => setStage(e.target.value)}
          size="small" fullWidth>
          <MenuItem value=""><em>— All —</em></MenuItem>
          {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code}</MenuItem>)}
        </TextField>
        <TextField label="Class Code" value={classCode} onChange={e => setClassCode(e.target.value)}
          size="small" fullWidth placeholder="e.g., 7A" />
        <TextField select label="Student Type" value={studentType} onChange={e => setStudentType(e.target.value)}
          size="small" fullWidth>
          <MenuItem value=""><em>— All —</em></MenuItem>
          <MenuItem value="Boarder">Boarder</MenuItem>
          <MenuItem value="Day Scholar">Day Scholar</MenuItem>
        </TextField>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2}>
        <Button variant="outlined"
          startIcon={previewing ? <CircularProgress size={16} /> : <Eye />}
          onClick={runPreview} disabled={previewing || running || !year || !term}>
          {previewing ? 'Previewing…' : 'Preview'}
        </Button>
        <Button variant="contained" color="success"
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : <CheckBox />}
          onClick={runUpdate} disabled={running || previewing || !year || !term}>
          {running ? 'Syncing…' : 'Sync Learning Areas'}
        </Button>
      </Box>

      {preview && preview.showPreview && preview.preview.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Learners to be updated (showing first 50):
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50', position: 'sticky', top: 0 }}>
                  <TableCell><strong>Adm No</strong></TableCell>
                  <TableCell><strong>Stage</strong></TableCell>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell align="right"><strong>Current</strong></TableCell>
                  <TableCell align="right"><strong>Add</strong></TableCell>
                  <TableCell align="right"><strong>Remove</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.preview.map((p, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Typography variant="caption" fontFamily="monospace">{p.admNo}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{p.stageCode}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{p.classCode}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{p.studentType}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2">{p.currentAreas}</Typography></TableCell>
                    <TableCell align="right">{p.missingAreas > 0 ? <Chip label={`+${p.missingAreas}`} size="small" color="success" variant="filled" /> : <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                    <TableCell align="right">{p.extraAreas > 0 ? <Chip label={`−${p.extraAreas}`} size="small" color="error" variant="filled" /> : <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {preview.preview.length < preview.toUpdate && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing {preview.preview.length} of {preview.toUpdate} learners to be updated
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Tab 3: Batch Invoice Generation ────────────────────────────────────────────

function BatchInvoiceTab({ academicYears }) {
  const [year,    setYear]    = useState('');
  const [term,    setTerm]    = useState('');
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  const run = async () => {
    if (!year || !term) { setError('Select academic year and term'); return; }
    setRunning(true); setError(''); setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/finance/batch-generate-term`,
        { academicYear: year, term }, { headers: getHeaders() });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Invoice generation failed');
    } finally { setRunning(false); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Generates fee invoices for all learners registered for the term but not yet invoiced.
        Uses each learner's fee template, posting group, and charge item GL accounts. Auto-applies any advance credits.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Run Batch Term Registration first to ensure all learners are registered before generating invoices.
      </Alert>

      {error  && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      <ResultSummary result={result} onClose={() => setResult(null)} />

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <TextField select label="Academic Year" value={year} onChange={e => setYear(e.target.value)}
          size="small" sx={{ minWidth: 160 }}>
          {academicYears.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
        </TextField>
        <TextField select label="Term" value={term} onChange={e => setTerm(e.target.value)}
          size="small" sx={{ minWidth: 120 }}>
          {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
        </TextField>
        <Button variant="contained" color="secondary"
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : <Receipt />}
          onClick={run} disabled={running || !year || !term}>
          {running ? 'Generating…' : 'Generate All Invoices'}
        </Button>
      </Box>
    </Box>
  );
}

// ── Tab 4: Learner Promotion ────────────────────────────────────────────────────

function PromotionTab({ academicYears, stages }) {
  const [fromStage,  setFromStage]  = useState('');
  const [toStage,    setToStage]    = useState('');
  const [year,       setYear]       = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selected,   setSelected]   = useState(new Set());
  const [loading,    setLoading]    = useState(false);
  const [running,    setRunning]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState('');

  const loadCandidates = useCallback(async () => {
    if (!fromStage) return;
    setLoading(true); setError(''); setCandidates([]); setSelected(new Set());
    try {
      const { data } = await axios.get(
        `${API}/api/academics/promotion-candidates?fromStage=${encodeURIComponent(fromStage)}`,
        { headers: getHeaders() }
      );
      setCandidates(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load candidates');
    } finally { setLoading(false); }
  }, [fromStage]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map(c => c.admNo)));
  };
  const toggle = (admNo) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(admNo) ? s.delete(admNo) : s.add(admNo);
      return s;
    });
  };

  const promote = async () => {
    if (!fromStage || !toStage || !year) { setError('Fill in From Stage, To Stage and Academic Year'); return; }
    if (selected.size === 0) { setError('Select at least one learner'); return; }
    setRunning(true); setError(''); setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/academics/promote`,
        { fromStage, toStage, academicYear: year, learnerAdmNos: [...selected] },
        { headers: getHeaders() });
      setResult(data);
      setCandidates([]); setSelected(new Set());
    } catch (err) {
      setError(err.response?.data?.error || 'Promotion failed');
    } finally { setRunning(false); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Promotes selected learners to the next stage. Clears class assignment so they can be reassigned in the new stage.
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Ensure class assignments for the target stage are complete before promoting. Promotion clears classCode.
      </Alert>

      {error  && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      <ResultSummary result={result} onClose={() => setResult(null)} />

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
        <TextField select label="From Stage" value={fromStage} onChange={e => setFromStage(e.target.value)}
          size="small" sx={{ minWidth: 180 }}>
          <MenuItem value=""><em>— Select —</em></MenuItem>
          {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
        </TextField>
        <TextField select label="To Stage" value={toStage} onChange={e => setToStage(e.target.value)}
          size="small" sx={{ minWidth: 180 }}>
          <MenuItem value=""><em>— Select —</em></MenuItem>
          {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
        </TextField>
        <TextField select label="Academic Year" value={year} onChange={e => setYear(e.target.value)}
          size="small" sx={{ minWidth: 160 }}>
          {academicYears.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
        </TextField>
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadCandidates}
          disabled={!fromStage || loading}>Refresh</Button>
        <Button variant="contained" color="warning" startIcon={<TrendingUp />}
          onClick={promote} disabled={running || selected.size === 0}>
          {running ? 'Promoting…' : `Promote (${selected.size})`}
        </Button>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={candidates.length > 0 && selected.size === candidates.length}
                  indeterminate={selected.size > 0 && selected.size < candidates.length}
                  onChange={toggleAll}
                  icon={<CheckBoxOutlineBlank />} checkedIcon={<CheckBox />}
                />
              </TableCell>
              <TableCell><strong>Adm No</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Stage</strong></TableCell>
              <TableCell><strong>Class</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    {fromStage ? 'No active learners in this stage' : 'Select a stage to view candidates'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : candidates.map(c => (
              <TableRow key={c.admNo} hover selected={selected.has(c.admNo)}>
                <TableCell padding="checkbox">
                  <Checkbox checked={selected.has(c.admNo)} onChange={() => toggle(c.admNo)} />
                </TableCell>
                <TableCell><Typography variant="caption" fontFamily="monospace">{c.admNo}</Typography></TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell><Chip label={c.stage} size="small" variant="outlined" /></TableCell>
                <TableCell>{c.classCode || <em style={{ color: '#bbb' }}>—</em>}</TableCell>
                <TableCell>{c.studentType}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {candidates.length > 0 && (
        <Typography variant="caption" color="text.secondary" mt={1} display="block">
          {candidates.length} learner(s) in this stage · {selected.size} selected
        </Typography>
      )}
    </Box>
  );
}

// ── Tab 5: Graduation ──────────────────────────────────────────────────────────

function GraduationTab({ academicYears, stages }) {
  const [stage,        setStage]        = useState('');
  const [year,         setYear]         = useState('');
  const [candidates,   setCandidates]   = useState([]);
  const [selected,     setSelected]     = useState(new Set());
  const [loading,      setLoading]      = useState(false);
  const [running,      setRunning]      = useState(false);
  const [result,       setResult]       = useState(null);
  const [error,        setError]        = useState('');
  const [graduateDlg,  setGraduateDlg]  = useState(false);

  const loadCandidates = useCallback(async () => {
    if (!stage) return;
    setLoading(true); setError(''); setCandidates([]); setSelected(new Set());
    try {
      const { data } = await axios.get(
        `${API}/api/academics/promotion-candidates?fromStage=${encodeURIComponent(stage)}`,
        { headers: getHeaders() }
      );
      setCandidates(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load candidates');
    } finally { setLoading(false); }
  }, [stage]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map(c => c.admNo)));
  };
  const toggle = (admNo) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(admNo) ? s.delete(admNo) : s.add(admNo);
      return s;
    });
  };

  const graduate = () => {
    if (!year) { setError('Select Academic Year'); return; }
    if (selected.size === 0) { setError('Select at least one learner'); return; }
    setGraduateDlg(true);
  };

  const confirmGraduate = async () => {
    setGraduateDlg(false);
    setRunning(true); setError(''); setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/academics/graduate`,
        { academicYear: year, learnerAdmNos: [...selected] },
        { headers: getHeaders() });
      setResult(data);
      setCandidates([]); setSelected(new Set());
    } catch (err) {
      setError(err.response?.data?.error || 'Graduation failed');
    } finally { setRunning(false); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Marks selected learners as Graduated and clears their class assignment.
        This is a permanent status change — only run for learners completing their final stage.
      </Typography>
      <Alert severity="error" sx={{ mb: 2 }}>
        Graduation is irreversible. Ensure all outstanding fees are settled before graduating learners.
      </Alert>

      {error  && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      <ResultSummary result={result} onClose={() => setResult(null)} />

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
        <TextField select label="Final Stage" value={stage} onChange={e => setStage(e.target.value)}
          size="small" sx={{ minWidth: 180 }}>
          <MenuItem value=""><em>— Select —</em></MenuItem>
          {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
        </TextField>
        <TextField select label="Academic Year" value={year} onChange={e => setYear(e.target.value)}
          size="small" sx={{ minWidth: 160 }}>
          {academicYears.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
        </TextField>
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadCandidates}
          disabled={!stage || loading}>Refresh</Button>
        <Button variant="contained" color="error" startIcon={<School />}
          onClick={graduate} disabled={running || selected.size === 0}>
          {running ? 'Graduating…' : `Graduate (${selected.size})`}
        </Button>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={candidates.length > 0 && selected.size === candidates.length}
                  indeterminate={selected.size > 0 && selected.size < candidates.length}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell><strong>Adm No</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Stage</strong></TableCell>
              <TableCell><strong>Class</strong></TableCell>
              <TableCell><strong>Posting Group</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    {stage ? 'No active learners in this stage' : 'Select a final stage to view candidates'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : candidates.map(c => (
              <TableRow key={c.admNo} hover selected={selected.has(c.admNo)}>
                <TableCell padding="checkbox">
                  <Checkbox checked={selected.has(c.admNo)} onChange={() => toggle(c.admNo)} />
                </TableCell>
                <TableCell><Typography variant="caption" fontFamily="monospace">{c.admNo}</Typography></TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell><Chip label={c.stage} size="small" variant="outlined" color="error" /></TableCell>
                <TableCell>{c.classCode || <em style={{ color: '#bbb' }}>—</em>}</TableCell>
                <TableCell>{c.postingGroup || 'DEFAULT'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {candidates.length > 0 && (
        <Typography variant="caption" color="text.secondary" mt={1} display="block">
          {candidates.length} learner(s) in this stage · {selected.size} selected
        </Typography>
      )}

      {/* Confirm Graduate */}
      <Dialog open={graduateDlg} onClose={() => setGraduateDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SchoolOutlined color="primary" />
            Graduate Learners
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Graduate <strong>{selected.size}</strong> learner(s)? Their status will be set to Graduated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGraduateDlg(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirmGraduate}>Graduate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

const PeriodicActivitiesPage = () => {
  const navigate = useNavigate();
  const [tab,          setTab]          = useState(0);
  const [academicYears, setAcademicYears] = useState([]);
  const [stages,       setStages]       = useState([]);

  useEffect(() => {
    const h = getHeaders();
    Promise.allSettled([
      axios.get(`${API}/api/academics/years`, { headers: h }),
      axios.get(`${API}/api/stages`, { headers: h }),
    ]).then(([ayRes, stRes]) => {
      if (ayRes.status === 'fulfilled') setAcademicYears(ayRes.value.data);
      if (stRes.status === 'fulfilled') setStages(stRes.value.data);
    });
  }, []);

  const TABS = [
    { label: 'Term Registration',         icon: <HowToReg /> },
    { label: 'Update Missing Areas',      icon: <CheckBox /> },
    { label: 'Invoice Generation',        icon: <Receipt /> },
    { label: 'Learner Promotion',         icon: <TrendingUp /> },
    { label: 'Graduation',                icon: <School /> },
  ];

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mb: 4, px: { xs: 1, sm: 2 } }}>
      <Card elevation={3} sx={{ mt: 2 }}>
        {/* Sticky Header */}
        <CardContent sx={{
          position: { xs: 'sticky', sm: 'relative' },
          top: { xs: 0, sm: 'auto' },
          zIndex: { xs: 10, sm: 'auto' },
          bgcolor: 'background.paper',
          pb: 1
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <PlayArrow sx={{ color: '#f57c00', flexShrink: 0 }} />
              <Typography variant="h5" sx={{ color: '#f57c00', fontWeight: 700, whiteSpace: 'nowrap', fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>
                Periodic Activities
              </Typography>
            </Box>
            <Tooltip title="Back to Academics">
              <IconButton onClick={() => navigate('/academics')}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, flexShrink: 0 }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="caption" color="text.secondary" mb={1.5} display="block" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Batch academic operations: register learners for a term, generate bulk invoices, promote to next stage, or graduate final-year learners.
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Scrollable Tabs on Mobile */}
          <Box sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-track': { bgcolor: 'grey.200' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 2 },
            mb: 1
          }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minWidth: 'fit-content',
                '& .MuiTab-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 12px', sm: '12px 16px' }
                }
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TABS.map((t, i) => (
                <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
              ))}
            </Tabs>
          </Box>
        </CardContent>

        <Paper variant="outlined" sx={{ m: 2, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          {tab === 0 && <BatchRegistrationTab academicYears={academicYears} />}
          {tab === 1 && <UpdateMissingAreasTab academicYears={academicYears} stages={stages} />}
          {tab === 2 && <BatchInvoiceTab      academicYears={academicYears} />}
          {tab === 3 && <PromotionTab         academicYears={academicYears} stages={stages} />}
          {tab === 4 && <GraduationTab        academicYears={academicYears} stages={stages} />}
        </Paper>
      </Card>
    </Box>
  );
};

export default PeriodicActivitiesPage;
